import { useQuery } from "@tanstack/react-query";
import { Eye, Puzzle, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Button, Card, Skeleton } from "@/components/ui";
import { api, canManage, type ModuleKey } from "@/lib/api";
import { priceText } from "@/pages/Modules";

import { t } from "@/lib/i18n";
/** Hat der Kunde die Funktion gebucht? (ohne feature: irgendein Paket des Moduls) – ohne Ausnahme für Logiful */
export function useLicensed(key: ModuleKey, feature?: string): boolean {
  const { tenant } = useTenant();
  const f = tenant.modules?.[key];
  return !!f && (!feature || f.includes(feature));
}

/** Darf die aktuelle Person die Funktion sehen? Logiful immer (Vorschau beim Kunden) */
export function useModuleFeature(key: ModuleKey, feature?: string): boolean {
  const { me } = useTenant();
  return useLicensed(key, feature) || me.user.isStaff;
}

/** Hinweis „Teil des Moduls …“ mit Nutzen, Preis und Weg zum Buchen */
export function ModuleRequired({ module, feature, compact }: { module: ModuleKey; feature?: string; compact?: boolean }) {
  const { tenantId, role } = useTenant();
  const q = useQuery({ queryKey: ["modules", tenantId], queryFn: () => api.modules.list(tenantId), staleTime: 60_000 });
  const m = q.data?.find((x) => x.key === module);
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  const featureLabel = feature && m ? m.featureLabels[feature] : null;
  const pkg = m?.packages.filter((p) => p.bookable && (!feature || p.features.includes(feature)))[0];
  return (
    <Card className={compact ? "p-4" : "p-6 max-w-2xl"}>
      <div className="flex items-start gap-3">
        <div className="size-10 shrink-0 rounded-xl bg-surface-2 grid place-items-center"><Puzzle className="size-5 text-fg-muted" /></div>
        <div className="min-w-0">
          <p className="font-semibold text-[15px]">{featureLabel ? <>„{featureLabel}{t("“ ist Teil des Moduls „")}{m?.name}“</> : <>{t("Modul „")}{m?.name ?? module}“</>}</p>
          {m && <p className="mt-1 text-[13px] text-fg-muted">{t(m.description)}</p>}
          {pkg ? (
            <p className="mt-2 text-[12.5px] text-fg-muted flex items-center gap-1.5"><Sparkles className="size-3.5 text-fg-subtle" /> {t("ab")} {priceText(pkg.price, m!.unit)}{pkg.trialDays && m?.trialAvailable ? t(" · {trialDays} Tage kostenlos testen", { trialDays: pkg.trialDays }) : ""}</p>
          ) : (
            <p className="mt-2 text-[12.5px] text-fg-muted">{t("Sprecht uns an – wir schalten es für euch frei.")}</p>
          )}
          <div className="mt-4">
            {canManage(role) ? (
              <Link to="/settings/modules"><Button variant="primary" size="sm">{pkg ? t("Ansehen & testen") : t("Zu den Modulen")}</Button></Link>
            ) : (
              <p className="text-[12.5px] text-fg-subtle">{t("Ein Administrator eures Teams kann es unter Einstellungen → Module buchen.")}</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Seite nur mit Modul: sonst Hinweis zum Buchen. Logiful sieht die Seite als Vorschau mit Hinweis, wenn der Kunde
 * das Modul nicht hat (Sperre auf dem Server gilt trotzdem – Einrichten nur mit Buchung).
 */
export function ModuleGate({ module, feature, title, children }: { module: ModuleKey; feature?: string; title: string; children: ReactNode }) {
  const { me } = useTenant();
  const licensed = useLicensed(module, feature);
  if (licensed) return <>{children}</>;
  if (me.user.isStaff)
    return (
      <>
        <div className="mx-4 md:mx-8 mt-4 flex items-center gap-2 rounded-xl border border-info/25 bg-info-soft/60 px-4 py-2.5 text-[12.5px]">
          <Eye className="size-4 text-info" /> {t("Vorschau für Logiful – der Kunde hat dieses Modul nicht gebucht. Freischalten unter")} <Link className="underline" to="/admin/modules">{t("Module & Preise")}</Link>.
        </div>
        {children}
      </>
    );
  return (
    <div className="pb-16">
      <PageHeader title={title} />
      <div className="px-4 md:px-8"><ModuleRequired module={module} {...(feature ? { feature } : {})} /></div>
    </div>
  );
}
